import useSWR from "swr";

import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import * as ReactDOM from "react-dom";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  LexicalTypeaheadMenuPlugin,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { $createTextNode, TextNode } from "lexical";
import { Account } from "models/account";
import { Entity } from "models/entity";
import { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "usehooks-ts";

import { $createMentionNodeFromMention } from "components/ContentEditor/nodes/MentionNode";
import SuggestionSnippet from "components/ContentEditor/SuggestionSnippet";
import { SKSpinner } from "components/customElements/skSpinner";
import { baseFetcher } from "utils/baseFetcher";

const PUNCTUATION =
  "\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%'\"~=<>_:;";
const NAME = "\\b[A-Z][^\\s" + PUNCTUATION + "]";

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const CapitalizedNameMentionsRegex = new RegExp(
  "(^|[^#])((?:" + DocumentMentionsRegex.NAME + "{" + 1 + ",})$)"
);

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ["@", "#", "$"].join("");

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = "[^" + TRIGGERS + PUNC + "\\s]";

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  "(?:" +
  "\\.[ |$]|" + // E.g. "r. " in "Mr. Smith"
  " |" + // E.g. " " in "Josh Duck"
  "[" +
  PUNC +
  "]|" + // E.g. "-' in "Salier-Hellendag"
  ")";

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    VALID_JOINS +
    "){0," +
    LENGTH_LIMIT +
    "})" +
    ")$"
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    "){0," +
    ALIAS_LENGTH_LIMIT +
    "})" +
    ")$"
);

function checkForCapitalizedNameMentions(
  text: string,
  minMatchLength: number
): MenuTextMatch | null {
  const match = CapitalizedNameMentionsRegex.exec(text);
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[2];
    if (matchingString != null && matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: matchingString,
      };
    }
  }
  return null;
}

function checkForAtSignMentions(
  text: string,
  minMatchLength: number
): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  const match = checkForAtSignMentions(text, 0);
  return match === null ? checkForCapitalizedNameMentions(text, 3) : match;
}

function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: any;
}) {
  let className = "item";
  if (isSelected) {
    className += " selected";
  }
  return (
    <li
      key={option.id}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={"typeahead-item-" + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <SuggestionSnippet
        mention={option}
        selectMention={onClick}
        onMouseEnter={onMouseEnter}
        isFocused={isSelected}
      />
    </li>
  );
}

export default function NewMentionsPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  const [trigger, setTrigger] = useState<string | null>(null);
  const [queryString, setQueryString] = useState<string | null>(null);
  const debounceQueryString = useDebounce(queryString, 200);

  // suggestions
  const suggestionsUrl = useMemo(() => {
    if (!debounceQueryString || debounceQueryString?.length <= 1) return;
    const query = debounceQueryString;

    if (trigger?.startsWith("@")) {
      return `/api/v2/insight-provider-profiles?filter[name]=${query}&sort=name`;
    } else if (trigger?.startsWith("$")) {
      return `/api/v2/entities?filter[fulltext]=${query}&sort=short-name`;
    } else if (trigger?.startsWith("#")) {
      return `/api/v2/search?filter%5Btype%5D=insight&include=primary-entity&query-text=${query}&search-type=mention`;
    }
  }, [debounceQueryString, trigger]);

  const { data: suggestionsData, isValidating: fetchingResults } = useSWR(
    suggestionsUrl,
    baseFetcher
  );
  const results = suggestionsData?.data || [];

  const triggerType = useMemo(() => {
    if (trigger?.startsWith("@")) {
      return "Insight Provider";
    }
    if (trigger?.startsWith("$")) {
      return "Entity";
    }
    if (trigger?.startsWith("#")) {
      return "Insight";
    }
  }, [trigger]);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
    minLength: 0,
  });

  const onSelectOption = useCallback(
    (
      mention: Insight | Entity | Account | any,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        let text = "";
        if (["accounts", "insight-providers"].includes(mention.type)) {
          text = mention.name;
        }
        if (mention.type === "entities") {
          text = mention.fulltext;
        }
        if (mention.type === "insights") {
          text = mention.tagline;
        }
        const linkNode = $createMentionNodeFromMention(mention);
        linkNode.append($createTextNode(text));

        if (nodeToReplace) {
          nodeToReplace.replace(linkNode);
        }

        const selection = linkNode.select();
        selection.insertText(" ");

        closeMenu();
      });
    },
    [editor]
  );

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      if (slashMatch !== null) {
        return null;
      }
      const queryMatch = getPossibleQueryMatch(text);
      setTrigger(queryMatch?.replaceableString || null);
      return queryMatch;
    },
    [checkForSlashTriggerMatch, editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin<any>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={results}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) =>
        anchorElementRef.current && !!triggerType
          ? ReactDOM.createPortal(
              <div
                className={twMerge(
                  "typeahead-popover mentions-menu z-[999]",
                  trigger?.startsWith("#") && "md:w-[600px]"
                )}
              >
                {fetchingResults || !queryString ? (
                  <div className="text-center text-inactive-gray text-[13px] p-[5px]">
                    <div>Type to cite an {triggerType}...</div>
                    <div>
                      <SKSpinner showLoading={false} />
                    </div>
                  </div>
                ) : !results?.length ? (
                  <div className="text-center text-inactive-gray text-[13px] italic p-[5px]">
                    <div>No results found</div>
                  </div>
                ) : (
                  <ul>
                    {results.map((option: any, i: number) => (
                      <MentionsTypeaheadMenuItem
                        index={i}
                        isSelected={selectedIndex === i}
                        onClick={() => {
                          setHighlightedIndex(i);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                        }}
                        key={option.id}
                        option={option}
                      />
                    ))}
                  </ul>
                )}
              </div>,
              anchorElementRef.current
            )
          : null
      }
    />
  );
}
